<template>
    <div class="dsp-root" data-testid="dsp-root">
        <input
            type="text"
            class="dsp-input"
            data-testid="dsp-input"
            :value="filterText"
            :placeholder="placeholder"
            @input="onInput"
        />
        <ul
            v-if="filtered.length"
            class="dsp-options"
            role="listbox"
            data-testid="dsp-options"
        >
            <li
                v-for="opt in filtered"
                :key="opt.id"
                class="dsp-option"
                :class="{ 'dsp-option--selected': isSelected(opt) }"
                :data-testid="`dsp-option-${opt.id}`"
                role="option"
                tabindex="0"
                @click="select(opt)"
                @keyup.enter="select(opt)"
                @keyup.space.prevent="select(opt)"
            >
                <span class="dsp-option__name">{{ opt.name }}</span>
                <span
                    v-if="opt.client_tag"
                    class="dsp-option__tag"
                    :data-testid="`dsp-tag-${opt.id}`"
                >
                    {{ opt.client_tag }}
                </span>
            </li>
        </ul>
        <div
            v-else
            class="dsp-empty"
            data-testid="dsp-empty"
        >
            No decoration sets match.
        </div>
    </div>
</template>

<script>
/**
 * DecorationSetPicker.vue -- shared typedown picker (Phase 4.8 / P4-Q3).
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 *
 * Used by:
 *   - BulkApplySetModal (P4.6) -- pick which set to apply to N products.
 *   - SaveAsSetButton (P4.9) -- pick an existing set to merge into.
 *
 * Behaviour
 * ---------
 * - Hydrates the full active set list from the backend on mount via
 *   ``useDecorationSetsStore.fetchList`` (capped at 100 per the backend
 *   max). For tenants with >100 sets the typedown still works because
 *   the server-side ``name`` ILIKE filter narrows server-side; this
 *   component issues that filter when the input text exceeds a short
 *   threshold AND the local list didn't shrink to a meaningful number.
 *   The first cut keeps it simple: hydrate up to perPage=100, filter
 *   client-side. A follow-up can wire server-side narrowing if any
 *   tenant grows past 100 sets.
 * - Filter is case-insensitive substring against ``name`` OR
 *   ``client_tag``.
 * - Sets whose ``client_tag`` matches the ``contextClientTag`` prop sort
 *   to the top; the rest follow in name order. Per P4-Q3 the picker
 *   surfaces ALL distributor sets but prioritises context matches.
 * - Selection emits ``update:modelValue`` with the set id. Suitable for
 *   ``v-model``.
 *
 * Props
 * -----
 *   modelValue      number | null   the currently-picked set id
 *   contextClientTag string | null  prioritise sets tagged for this client
 *   placeholder     string          override the input placeholder copy
 */
import { useDecorationSetsStore } from '@/stores/decorationSets'

export default {
    name: 'DecorationSetPicker',

    props: {
        modelValue: {
            type: [Number, String, null],
            default: null,
        },
        contextClientTag: {
            type: [String, null],
            default: null,
        },
        placeholder: {
            type: String,
            default: 'Filter sets by name or client...',
        },
    },

    emits: ['update:modelValue'],

    data() {
        return {
            store: useDecorationSetsStore(),
            filterText: '',
        }
    },

    computed: {
        filtered() {
            const text = (this.filterText || '').trim().toLowerCase()
            const all = Array.isArray(this.store.sets) ? this.store.sets : []
            const matches = text
                ? all.filter((s) => {
                      const name = (s.name || '').toLowerCase()
                      const tag = (s.client_tag || '').toLowerCase()
                      return (
                          name.includes(text) || (!!tag && tag.includes(text))
                      )
                  })
                : all.slice()

            const ctxTag =
                typeof this.contextClientTag === 'string'
                    ? this.contextClientTag.trim().toLowerCase()
                    : ''
            if (!ctxTag) {
                return matches.sort((a, b) =>
                    String(a.name || '').localeCompare(String(b.name || '')),
                )
            }
            // Stable two-bucket sort: ctx-tagged first, then the rest.
            // Within each bucket, alpha by name.
            const head = []
            const tail = []
            for (const s of matches) {
                const tag = (s.client_tag || '').toLowerCase()
                if (tag === ctxTag) {
                    head.push(s)
                } else {
                    tail.push(s)
                }
            }
            const byName = (a, b) =>
                String(a.name || '').localeCompare(String(b.name || ''))
            head.sort(byName)
            tail.sort(byName)
            return [...head, ...tail]
        },
    },

    async mounted() {
        // Hydrate at the backend max (100) so a single roundtrip covers
        // most tenants. Hot path for the typedown is local filter.
        if (!this.store.sets.length) {
            try {
                await this.store.fetchList({ page: 1, perPage: 100 })
            } catch (_e) {
                // Store flips ``error`` already; empty list renders the
                // ``no sets match`` placeholder which is the right UX.
            }
        }
    },

    methods: {
        onInput(event) {
            this.filterText = event?.target?.value ?? ''
        },

        isSelected(opt) {
            if (this.modelValue == null) return false
            return String(opt.id) === String(this.modelValue)
        },

        select(opt) {
            // Always emit a number when the id is numeric so v-model on
            // a numeric backing field doesn't drift to string. Falls
            // through to whatever id type the row carries otherwise.
            const raw = opt?.id
            const coerced =
                typeof raw === 'string' && /^\d+$/.test(raw)
                    ? Number(raw)
                    : raw
            this.$emit('update:modelValue', coerced)
        },
    },
}
</script>

<style scoped>
.dsp-root {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.dsp-input {
    width: 100%;
    padding: 6px 10px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.dsp-options {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
}

.dsp-option {
    padding: 6px 10px;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    font-size: 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.dsp-option:last-child {
    border-bottom: 0;
}

.dsp-option:hover,
.dsp-option:focus {
    background: rgba(25, 118, 210, 0.08);
    outline: none;
}

.dsp-option--selected {
    background: rgba(25, 118, 210, 0.16);
    font-weight: 600;
}

.dsp-option__name {
    flex: 1 1 auto;
}

.dsp-option__tag {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.65);
}

.dsp-empty {
    padding: 12px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 14px;
    border: 1px dashed rgba(0, 0, 0, 0.12);
    border-radius: 4px;
}
</style>
